import React, { useState, useEffect } from 'react';
import { useStore } from '../context/StoreContext';
import { X, Trash2, Plus, Minus, ShoppingCart, ArrowRight, Tag, ShieldCheck, Truck, ExternalLink } from 'lucide-react';
import { getOptimizedImageUrl, getItemDisplayImage } from '../utils/imageOptimizer';
import { AnimatePresence, motion } from 'motion/react';

interface CartDrawerProps {
  onNavigate: (path: string) => void;
}

export const CartDrawer: React.FC<CartDrawerProps> = ({ onNavigate }) => {
  const {
    cart,
    cartDrawerOpen,
    setCartDrawerOpen,
    updateCartQty,
    removeFromCart,
    settings,
    appliedCoupon,
    couponDiscount,
    applyCoupon,
    removeCoupon,
    user,
    showToast
  } = useStore();

  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState<string | null>(null);
  const [loadingCoupon, setLoadingCoupon] = useState(false);

  // Lock body scroll when cart drawer is open to prevent background scrolling
  useEffect(() => {
    if (cartDrawerOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [cartDrawerOpen]);

  const subtotal = cart.reduce((acc, item) => {
    const price = item.variant.discountPrice || item.variant.price || item.product.discountPrice || item.product.basePrice;
    return acc + price * item.quantity;
  }, 0);

  const freeShippingThreshold = typeof settings.freeShippingThreshold === 'number' ? settings.freeShippingThreshold : 999;
  const standardShippingFee = typeof settings.standardShippingFee === 'number' ? settings.standardShippingFee : 79;
  const remainingForFreeShipping = Math.max(0, freeShippingThreshold - subtotal);
  const freeShippingPercent = freeShippingThreshold > 0 ? Math.min(100, (subtotal / freeShippingThreshold) * 100) : 100;

  const shippingCharge = remainingForFreeShipping === 0 ? 0 : standardShippingFee;
  const finalTotal = Math.max(0, subtotal - couponDiscount + shippingCharge);

  const unavailableCartItems = React.useMemo(() => {
    return cart.filter(item => {
      const variantStock = typeof item.variant?.stock === 'number' ? item.variant.stock : undefined;
      const productStock = typeof item.product?.stock === 'number' ? item.product.stock : undefined;
      return item.product?.status === 'out_of_stock' || (variantStock !== undefined && variantStock <= 0) || (productStock !== undefined && productStock <= 0);
    });
  }, [cart]);
  const hasUnavailableItems = unavailableCartItems.length > 0;

  const handleApplyCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!couponCode.trim()) return;
    setLoadingCoupon(true);
    setCouponError(null);
    const res = await applyCoupon(couponCode);
    setLoadingCoupon(false);
    if (!res.success) {
      setCouponError(res.message);
    } else {
      setCouponCode('');
    }
  };

  return (
    <AnimatePresence>
      {cartDrawerOpen && (
    <div className="fixed inset-0 z-50 overflow-hidden text-left select-none">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-xs"
        onClick={() => setCartDrawerOpen(false)}
      />

      <div className="fixed inset-y-0 right-0 max-w-full flex sm:pl-10 z-50">
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'tween', duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md bg-white shadow-2xl flex flex-col h-[100dvh] max-h-[100dvh] overflow-hidden">
          {/* Header */}
          <div className="p-3.5 sm:p-4 border-b border-stone-200 flex items-center justify-between bg-stone-50 shrink-0">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-[#C0654B]" />
              <h2 className="text-sm sm:text-base font-bold text-stone-900 font-serif">Shopping Bag</h2>
              <span className="bg-[#F3E9E4] text-[#C0654B] font-bold text-xs px-2 py-0.5 rounded-full">
                {cart.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {cart.length > 0 && (
                <button
                  onClick={() => {
                    setCartDrawerOpen(false);
                    onNavigate('/cart');
                  }}
                  className="text-[11px] font-bold text-[#C0654B] hover:underline flex items-center gap-1 cursor-pointer pr-1"
                  title="View Full Cart Page"
                >
                  <span>Full View</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={() => setCartDrawerOpen(false)}
                className="p-1.5 text-stone-500 hover:text-stone-900 rounded-full hover:bg-stone-200/60 cursor-pointer"
                aria-label="Close cart"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* FREE SHIPPING PROGRESS BAR */}
          <div className="bg-[#F3E9E4]/90 p-2.5 px-4 border-b border-stone-200/80 shrink-0">
            <div className="flex items-center justify-between text-[11px] font-semibold text-stone-800 mb-1">
              <span className="flex items-center gap-1.5 text-[#C0654B]">
                <Truck className="w-3.5 h-3.5" />
                {remainingForFreeShipping > 0 ? (
                  <span>Add <strong>₹{remainingForFreeShipping}</strong> for <strong>FREE Delivery</strong></span>
                ) : (
                  <span className="text-emerald-700 font-bold">🎉 FREE Delivery unlocked!</span>
                )}
              </span>
            </div>
            <div className="w-full bg-stone-200 h-1.5 rounded-full overflow-hidden">
              <div
                className="bg-[#C0654B] h-full transition-all duration-300 rounded-full"
                style={{ width: `${freeShippingPercent}%` }}
              />
            </div>
          </div>

          {/* CART ITEMS LIST */}
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-4 space-y-2.5">
            {cart.length === 0 ? (
              <div className="text-center py-16 space-y-4">
                <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto text-stone-400">
                  <ShoppingCart className="w-8 h-8" />
                </div>
                <div>
                  <p className="font-bold text-stone-800 text-base">Your Cart is Empty</p>
                  <p className="text-xs text-stone-500 mt-1">Explore our latest terracotta ethnic & western collection</p>
                </div>
                <button
                  onClick={() => {
                    setCartDrawerOpen(false);
                    onNavigate('/category/women');
                  }}
                  className="bg-[#C0654B] text-white text-xs font-bold px-6 py-2.5 rounded-lg shadow-md hover:bg-[#8B4A38] transition-colors cursor-pointer"
                >
                  START SHOPPING
                </button>
              </div>
            ) : (
              cart.map((item) => {
                const price = item.variant.discountPrice || item.variant.price || item.product.discountPrice || item.product.basePrice;
                const rawImage = getItemDisplayImage(item.product, item.selectedColor, item.variant);
                const image = getOptimizedImageUrl(rawImage, { width: 200, quality: 75 });
                const variantStock = typeof item.variant?.stock === 'number' ? item.variant.stock : undefined;
                const productStock = typeof item.product?.stock === 'number' ? item.product.stock : undefined;
                const isItemOutOfStock = item.product?.status === 'out_of_stock' || (variantStock !== undefined && variantStock <= 0) || (productStock !== undefined && productStock <= 0);

                return (
                  <div key={item.id} className={`flex gap-2.5 p-2.5 rounded-xl border transition-all ${
                    isItemOutOfStock 
                      ? 'bg-red-50/50 border-red-200' 
                      : 'bg-stone-50/60 border-stone-200/80 hover:bg-stone-50'
                  }`}>
                    <img
                      src={image}
                      alt={item.product.name || 'Product'}
                      onClick={() => {
                        setCartDrawerOpen(false);
                        onNavigate(`/product/${item.product.id}`);
                      }}
                      className="w-16 h-20 object-cover object-top rounded-lg bg-white shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        const target = e.currentTarget;
                        if (!target.src.includes('images.unsplash.com')) {
                          target.src = 'https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=600&q=80';
                        }
                      }}
                    />
                    <div className="flex-1 flex flex-col justify-between min-w-0">
                      <div>
                        <div className="flex justify-between items-start gap-1">
                          <h4 
                            onClick={() => {
                              setCartDrawerOpen(false);
                              onNavigate(`/product/${item.product.id}`);
                            }}
                            className="text-xs font-bold text-stone-900 truncate pr-1 cursor-pointer hover:text-[#C0654B] transition-colors"
                          >
                            {item.product.name}
                          </h4>
                          <button
                            onClick={() => removeFromCart(item.id)}
                            className="text-stone-400 hover:text-red-500 transition-colors p-0.5 cursor-pointer shrink-0"
                            aria-label="Remove item"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <p className="text-[11px] text-stone-500 mt-0.5">
                          Size: <strong className="text-stone-800">{item.selectedSize}</strong> | Color: <strong className="text-stone-800">{item.selectedColor}</strong>
                        </p>
                        {isItemOutOfStock && (
                          <span className="inline-block mt-0.5 text-[9px] font-bold text-red-700 bg-red-100 border border-red-200 px-1 py-0.2 rounded">
                            Out of Stock
                          </span>
                        )}
                      </div>

                      <div className="flex items-center justify-between mt-1.5">
                        {/* Quantity Stepper */}
                        <div className="flex items-center border border-stone-300 rounded-md bg-white">
                          <button
                            onClick={() => updateCartQty(item.id, item.quantity - 1)}
                            className="px-1.5 py-0.5 text-stone-600 hover:text-stone-900 cursor-pointer"
                          >
                            <Minus className="w-2.5 h-2.5" />
                          </button>
                          <span className="px-2 text-xs font-bold text-stone-800">{item.quantity}</span>
                          <button
                            disabled={isItemOutOfStock}
                            onClick={() => updateCartQty(item.id, item.quantity + 1)}
                            className={`px-1.5 py-0.5 ${isItemOutOfStock ? 'text-stone-300 cursor-not-allowed' : 'text-stone-600 hover:text-stone-900 cursor-pointer'}`}
                          >
                            <Plus className="w-2.5 h-2.5" />
                          </button>
                        </div>

                        <span className="font-extrabold text-stone-900 text-xs sm:text-sm">
                          ₹{(price * item.quantity).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* SUMMARY & CHECKOUT FOOTER */}
          {cart.length > 0 && (
            <div className="p-3 sm:p-3.5 border-t border-stone-200 bg-stone-50/95 backdrop-blur-xs space-y-2.5 pb-safe shrink-0">
              {/* Unavailable Items Warning */}
              {hasUnavailableItems && (
                <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-[11px] text-red-700 font-bold flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0"></span>
                  <span>Remove unavailable items to proceed with checkout.</span>
                </div>
              )}

              {/* COUPON SECTION */}
              {appliedCoupon ? (
                <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded-lg text-[11px]">
                  <div className="flex items-center gap-1.5 text-emerald-800 font-medium truncate">
                    <Tag className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span className="truncate">Promo <strong>{appliedCoupon.code}</strong> (-₹{couponDiscount})</span>
                  </div>
                  <button
                    onClick={removeCoupon}
                    className="text-emerald-700 hover:underline font-bold cursor-pointer shrink-0 ml-1"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <form onSubmit={handleApplyCoupon} className="flex gap-1.5">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="Coupon Code"
                    className="flex-1 text-xs border border-stone-300 rounded-lg px-2.5 py-1.5 uppercase tracking-wider focus:outline-none focus:border-[#C0654B] bg-white"
                  />
                  <button
                    type="submit"
                    disabled={loadingCoupon || !couponCode.trim()}
                    className="bg-stone-900 hover:bg-[#C0654B] text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                  >
                    Apply
                  </button>
                </form>
              )}
              {couponError && <p className="text-[10px] text-red-600 font-medium">{couponError}</p>}

              {/* PRICE DETAILS BREAKDOWN */}
              <div className="bg-white p-2.5 rounded-lg border border-stone-200 space-y-1.5 text-[11px] text-stone-600">
                <div className="flex justify-between items-center">
                  <span>Subtotal ({cart.length} {cart.length === 1 ? 'item' : 'items'})</span>
                  <span className="font-semibold text-stone-900">₹{subtotal.toLocaleString('en-IN')}</span>
                </div>
                {couponDiscount > 0 && (
                  <div className="flex justify-between items-center text-emerald-700 font-bold">
                    <span>Coupon Discount</span>
                    <span>-₹{couponDiscount.toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span>Shipping</span>
                  <span className={shippingCharge === 0 ? 'text-emerald-700 font-bold' : 'font-semibold text-stone-900'}>
                    {shippingCharge === 0 ? 'FREE' : `₹${shippingCharge}`}
                  </span>
                </div>
                <div className="flex justify-between items-baseline font-black text-stone-900 text-xs sm:text-sm pt-1.5 border-t border-stone-100">
                  <span>Total Amount</span>
                  <span className="text-[#C0654B]">₹{finalTotal.toLocaleString('en-IN')}</span>
                </div>
              </div>

              {/* CHECKOUT BUTTON (Place Order) */}
              <button
                disabled={hasUnavailableItems}
                onClick={() => {
                  if (hasUnavailableItems) return;
                  setCartDrawerOpen(false);
                  if (!user) {
                    showToast('Please sign in or create an account to place your order.');
                    onNavigate('/account?redirect=/checkout');
                  } else {
                    onNavigate('/checkout');
                  }
                }}
                className={`w-full text-white text-xs font-extrabold py-2.5 rounded-lg shadow-2xs flex items-center justify-center gap-1.5 transition-colors uppercase tracking-wider ${
                  hasUnavailableItems
                    ? 'bg-stone-300 text-stone-500 cursor-not-allowed opacity-70'
                    : 'bg-[#C0654B] hover:bg-[#a85239] cursor-pointer'
                }`}
              >
                <span>{hasUnavailableItems ? 'Remove Unavailable Items' : 'Proceed to Checkout'}</span>
                {!hasUnavailableItems && <ArrowRight className="w-3.5 h-3.5" />}
              </button>

              <div className="flex items-center justify-center gap-1 text-[10px] text-stone-400">
                <ShieldCheck className="w-3 h-3 text-emerald-600" />
                <span>100% Safe Payments & Authentic Quality Guaranteed</span>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
      )}
    </AnimatePresence>
  );
};
